/* ==========================================================================
   ANNIE'S IBIZA — COLLECTION FILTERING
   Progressive enhancement over Shopify's native storefront filtering.
   No dependencies. Defer-load it.
   ========================================================================== */

(function () {
  'use strict';

  const SELECTORS = {
    form: '[data-af-form]',
    drawer: '[data-af-drawer]',
    scrim: '[data-af-scrim], .af__scrim',
    open: '[data-af-open]',
    close: '[data-af-close]',
    sort: '[data-af-sort]',
    sortInput: '[data-af-sort-input]',
    clear: '[data-af-clear]',
    remove: '[data-af-remove]',
    find: '[data-af-find]',
    findable: '[data-af-findable]',
    noMatch: '[data-af-no-match]',
    price: '[data-af-price]'
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  class AnniesFacets extends HTMLElement {
    connectedCallback() {
      if (this.shellBound) return;
      this.shellBound = true;

      this.sectionId = this.dataset.sectionId;
      this.gridSelector =
        this.dataset.gridSelector || '#main-collection-product-grid, #pagination';
      this.debounce = null;
      this.controller = null;

      /* Bound once — the element itself survives every re-render, so these
         listeners must never be attached twice. */
      this.onFormChange = this.onFormChange.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);

      this.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);

      this.bindContent();
      this.setupStickyOffset();

      if (!AnniesFacets.historyBound) {
        AnniesFacets.historyBound = true;
        window.addEventListener('popstate', () => {
          const root = document.querySelector('annies-facets');
          if (root) root.render(window.location.search, { push: false });
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.documentElement.classList.remove('af-is-locked');
    }

    /* Re-queries the markup and wires the controls inside it. Safe to call
       again after the facets have been replaced by a filter request. */
    bindContent() {
      this.form = this.querySelector(SELECTORS.form);
      this.drawer = this.querySelector(SELECTORS.drawer);
      this.scrim = this.querySelector('.af__scrim');

      if (this.form) {
        this.form.addEventListener('change', this.onFormChange);
        this.form.addEventListener('input', this.onFormChange);
        this.form.addEventListener('submit', this.onFormSubmit);
      }

      this.setupSort();
      this.setupFind();
      this.setupPrice();

      /* If the drawer was open before a re-render, put it straight back. */
      if (AnniesFacets.wasOpen && this.drawer) {
        this.drawer.hidden = false;
        if (this.scrim) this.scrim.hidden = false;
        this.classList.add('af--open');
        const trigger = this.querySelector(SELECTORS.open);
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
      }
    }

    /* ---------------------------------------------------------------- drawer */

    get isOpen() {
      return this.classList.contains('af--open');
    }

    openDrawer(opts = {}) {
      if (!this.drawer) return;
      this.previousFocus = opts.restoreFocus === false ? null : document.activeElement;

      this.drawer.hidden = false;
      if (this.scrim) this.scrim.hidden = false;

      /* Flush layout so the transform has a start value to animate from.
         A forced reflow rather than requestAnimationFrame — rAF is suspended
         in hidden tabs, which would leave the panel stuck off-screen on a
         restored session. */
      void this.drawer.offsetWidth;

      this.classList.add('af--open');
      document.documentElement.classList.add('af-is-locked');

      const trigger = this.querySelector(SELECTORS.open);
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      AnniesFacets.wasOpen = true;

      if (opts.restoreFocus !== false) {
        const first = this.drawer.querySelector(FOCUSABLE);
        if (first) first.focus({ preventScroll: true });
      }
    }

    closeDrawer() {
      if (!this.drawer || !this.isOpen) return;

      this.classList.remove('af--open');
      document.documentElement.classList.remove('af-is-locked');
      AnniesFacets.wasOpen = false;

      const trigger = this.querySelector(SELECTORS.open);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      const finish = () => {
        if (this.isOpen) return;
        this.drawer.hidden = true;
        if (this.scrim) this.scrim.hidden = true;
      };
      /* match the CSS transition, but never hang if it doesn't fire */
      this.drawer.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 700);

      if (this.previousFocus && document.contains(this.previousFocus)) {
        this.previousFocus.focus({ preventScroll: true });
      } else if (trigger) {
        trigger.focus({ preventScroll: true });
      }
    }

    onDocumentClick(event) {
      if (event.target.closest(SELECTORS.open)) {
        event.preventDefault();
        this.isOpen ? this.closeDrawer() : this.openDrawer();
        return;
      }

      if (event.target.closest(SELECTORS.close)) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      /* Active pills and "Clear all" are real links — hijack for async */
      const link = event.target.closest(`${SELECTORS.remove}, ${SELECTORS.clear}`);
      if (link && link.href) {
        event.preventDefault();
        const search = new URL(link.href, window.location.origin).search;
        this.render(search);
      }
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.stopPropagation();
        this.closeDrawer();
        return;
      }

      if (event.key !== 'Tab' || !this.drawer) return;

      const nodes = Array.from(this.drawer.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (!nodes.length) return;

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ------------------------------------------------------------------ sort */

    setupSort() {
      const select = this.querySelector(SELECTORS.sort);
      const hidden = this.querySelector(SELECTORS.sortInput);
      if (!select) return;

      select.addEventListener('change', () => {
        if (hidden) hidden.value = select.value;
        this.submit();
      });
    }

    /* ------------------------------------------------- find-a-designer */

    setupFind() {
      this.querySelectorAll(SELECTORS.find).forEach((input) => {
        const panel = input.closest('.af__group-panel');
        if (!panel) return;
        const list = panel.querySelector(SELECTORS.findable);
        const empty = panel.querySelector(SELECTORS.noMatch);
        if (!list) return;

        const rows = Array.from(list.children);

        input.addEventListener('input', () => {
          const q = input.value.trim().toLowerCase();
          let shown = 0;

          rows.forEach((row) => {
            const term = row.dataset.afFindTerm || row.textContent.toLowerCase();
            const match = !q || term.indexOf(q) !== -1;
            row.hidden = !match;
            if (match) shown += 1;
          });

          if (empty) empty.hidden = shown !== 0;
        });

        /* keep Enter inside the drawer instead of submitting the form */
        input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') event.preventDefault();
        });
      });
    }

    /* ----------------------------------------------------------------- price */

    setupPrice() {
      this.querySelectorAll(SELECTORS.price).forEach((wrap) => {
        const max = parseInt(wrap.dataset.priceMax, 10) || 0;
        const minInput = wrap.querySelector('[data-af-price-min]');
        const maxInput = wrap.querySelector('[data-af-price-max]');
        const minRange = wrap.querySelector('[data-af-slider-min]');
        const maxRange = wrap.querySelector('[data-af-slider-max]');
        const fill = wrap.querySelector('[data-af-slider-fill]');
        if (!minInput || !maxInput || !minRange || !maxRange) return;

        const paint = () => {
          const lo = parseFloat(minRange.value) || 0;
          const hi = parseFloat(maxRange.value);
          const hiVal = isNaN(hi) ? max : hi;
          if (fill && max > 0) {
            fill.style.left = `${(lo / max) * 100}%`;
            fill.style.right = `${100 - (hiVal / max) * 100}%`;
          }
        };

        /* Blank inputs mean "unbounded" — never send 0 / max to Shopify,
           it produces a filter pill for a filter the customer didn't set. */
        const normalise = () => {
          if (parseFloat(minInput.value) <= 0) minInput.value = '';
          if (parseFloat(maxInput.value) >= max) maxInput.value = '';
        };

        minRange.addEventListener('input', () => {
          if (parseFloat(minRange.value) > parseFloat(maxRange.value) - 1) {
            minRange.value = Math.max(0, parseFloat(maxRange.value) - 1);
          }
          minInput.value = minRange.value;
          normalise();
          paint();
        });

        maxRange.addEventListener('input', () => {
          if (parseFloat(maxRange.value) < parseFloat(minRange.value) + 1) {
            maxRange.value = Math.min(max, parseFloat(minRange.value) + 1);
          }
          maxInput.value = maxRange.value;
          normalise();
          paint();
        });

        minInput.addEventListener('input', () => {
          const v = minInput.value === '' ? 0 : Math.min(parseFloat(minInput.value) || 0, max);
          minRange.value = v;
          paint();
        });

        maxInput.addEventListener('input', () => {
          const v = maxInput.value === '' ? max : Math.min(parseFloat(maxInput.value) || 0, max);
          maxRange.value = v;
          paint();
        });

        paint();
        wrap.afPaint = paint;
      });
    }

    /* -------------------------------------------------------------- sticky */

    setupStickyOffset() {
      /* Sit the bar below a sticky header if the theme has one. */
      const header =
        document.querySelector('.shopify-section-header-sticky') ||
        document.querySelector('sticky-header') ||
        document.querySelector('[data-sticky-header]');
      if (!header) return;
      const h = header.getBoundingClientRect().height;
      if (h > 0) this.style.setProperty('--af-sticky-offset', `${Math.round(h)}px`);
    }

    /* ------------------------------------------------------------- fetching */

    onFormChange(event) {
      const t = event.target;
      if (!t) return;
      if (t.matches(SELECTORS.find)) return;      // typing in the designer search
      if (t.matches(SELECTORS.sort)) return;      // handled by setupSort
      if (t.type === 'range') {
        /* wait for the drag to settle */
        this.submit(500);
        return;
      }
      this.submit();
    }

    onFormSubmit(event) {
      event.preventDefault();
      this.submit(0);
    }

    buildSearch() {
      const data = new FormData(this.form);
      const params = new URLSearchParams();

      for (const [key, value] of data.entries()) {
        if (value === null || String(value).trim() === '') continue;
        params.append(key, String(value).trim());
      }
      return params.toString();
    }

    submit(delay = 260) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.render(`?${this.buildSearch()}`), delay);
    }

    /* gridSelector may list more than one target, because this theme keeps
       the product grid and the pagination as siblings rather than in one
       wrapper. Each selector is swapped independently. */
    gridSelectors() {
      return this.gridSelector
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }

    gridNodes() {
      const nodes = [];
      this.gridSelectors().forEach((sel) => {
        const el = document.querySelector(sel);
        if (el) nodes.push({ sel, el });
      });
      return nodes;
    }

    /* the first target is what we scroll to and mark busy */
    grid() {
      const nodes = this.gridNodes();
      return nodes.length ? nodes[0].el : null;
    }

    setLoading(state) {
      this.gridNodes().forEach(({ el }) => el.classList.toggle('af-grid-loading', state));
    }

    render(search, opts = {}) {
      const push = opts.push !== false;
      const base = window.location.pathname;
      const query = (search || '').replace(/^\?/, '');

      this.setLoading(true);
      this.setBusy(true);

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const url = `${base}?${query}${query ? '&' : ''}section_id=${this.sectionId}`;

      fetch(url, { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Filter request failed (${response.status})`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');

          /* 1. swap the product grid, and the pagination beside it */
          this.gridSelectors().forEach((sel) => {
            const live = document.querySelector(sel);
            const fresh = doc.querySelector(sel);
            if (!live) return;
            live.innerHTML = fresh ? fresh.innerHTML : '';
          });

          /* 2. swap the facets themselves so counts and states stay honest */
          const freshFacets = doc.querySelector('annies-facets');
          if (freshFacets) {
            const openGroups = Array.from(this.querySelectorAll('[data-af-group]')).map(
              (g) => g.open
            );
            const oldBody = this.drawer && this.drawer.querySelector('.af__drawer-body');
            const scrollTop = oldBody ? oldBody.scrollTop : 0;
            const focusName = document.activeElement && document.activeElement.name;

            this.innerHTML = freshFacets.innerHTML;
            this.bindContent();

            /* restore what the customer had open, and where they were */
            this.querySelectorAll('[data-af-group]').forEach((g, i) => {
              if (typeof openGroups[i] === 'boolean') g.open = openGroups[i];
            });
            const body = this.drawer && this.drawer.querySelector('.af__drawer-body');
            if (body) body.scrollTop = scrollTop;
            if (focusName && this.isOpen) {
              const again = this.querySelector(`[name="${focusName}"]`);
              if (again) again.focus({ preventScroll: true });
            }
          }

          if (push) {
            window.history.pushState({ search: query }, '', `${base}${query ? `?${query}` : ''}`);
          }

          this.setLoading(false);
          this.setBusy(false);
          this.scrollIntoViewIfNeeded();
          this.announce();
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.error('[annies-facets]', error);
          this.setLoading(false);
          this.setBusy(false);
          /* last resort: a normal page load always works */
          window.location.href = `${base}${query ? `?${query}` : ''}`;
        });
    }

    setBusy(state) {
      const grid = this.grid();
      if (grid) grid.setAttribute('aria-busy', state ? 'true' : 'false');
    }

    scrollIntoViewIfNeeded() {
      if (this.isOpen) return;
      const top = this.getBoundingClientRect().top;
      if (top < 0) {
        const offset = parseInt(getComputedStyle(this).getPropertyValue('--af-sticky-offset'), 10) || 0;
        window.scrollTo({
          top: window.scrollY + top - offset - 8,
          behavior: 'smooth'
        });
      }
    }

    announce() {
      const count = this.querySelector('[data-af-count]');
      if (count) count.setAttribute('aria-live', 'polite');
    }
  }

  AnniesFacets.wasOpen = false;
  AnniesFacets.historyBound = false;

  if (!window.customElements.get('annies-facets')) {
    window.customElements.define('annies-facets', AnniesFacets);
  }
})();